"use client";

import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
  type ReactNode,
} from "react";
import { defaultUserSettings } from "@/lib/user/default-user-settings";
import {
  defaultLocationCoordinateDisplays,
  hasAtLeastOneCoordinateDisplay,
  type LocationCoordinateDisplays,
} from "@/lib/geo/location-coordinate-display";
import {
  isProjectDisplaySettingKey,
  type ProjectDisplayUserSlice,
} from "@/lib/project/project-display-settings-map";
import type { UserSettings } from "@/types/user-settings";
import { STORAGE_KEY } from "@/constants/storage-keys";

export type { UserSettings } from "@/types/user-settings";

type ForcedProjectDisplayState =
  | { force: true; slice: ProjectDisplayUserSlice }
  | { force: false; slice: null };

type UserProfileContextValue = {
  settings: UserSettings;
  updateSetting: <K extends keyof UserSettings>(key: K, value: UserSettings[K]) => void;
  resetSettings: () => void;
  syncForcedProjectDisplay: (payload: ForcedProjectDisplayState) => void;
};

const UserProfileContext = createContext<UserProfileContextValue | null>(null);

function loadSettings(): UserSettings {
  if (typeof window === "undefined") return defaultUserSettings;
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) return defaultUserSettings;
    const parsed = JSON.parse(stored) as Partial<UserSettings>;
    const merged: UserSettings = {
      ...defaultUserSettings,
      ...parsed,
      locationCoordinateDisplays: {
        ...defaultLocationCoordinateDisplays,
        ...parsed.locationCoordinateDisplays,
      },
    };
    if (!hasAtLeastOneCoordinateDisplay(merged.locationCoordinateDisplays)) {
      merged.locationCoordinateDisplays = {
        ...defaultLocationCoordinateDisplays,
      };
    }
    if (
      merged.timezone !== "local" &&
      merged.timezone !== "utc" &&
      merged.timezone !== "at_location"
    ) {
      merged.timezone = defaultUserSettings.timezone;
    }
    return merged;
  } catch {
    return defaultUserSettings;
  }
}

function saveSettings(settings: UserSettings) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
  } catch {
    // localStorage unavailable
  }
}

export function UserProfileProvider({ children }: { children: ReactNode }) {
  const [ready, setReady] = useState(false);
  /** User-owned settings (localStorage). */
  const [storageSettings, setStorageSettings] = useState<UserSettings>(defaultUserSettings);
  /** When `force`, merged settings use `slice` for project display keys. */
  const [forcedProjectDisplay, setForcedProjectDisplay] =
    useState<ForcedProjectDisplayState | null>(null);

  useEffect(() => {
    requestAnimationFrame(() => {
      setStorageSettings(loadSettings());
      setReady(true);
    });
  }, []);

  const settings = useMemo((): UserSettings => {
    if (forcedProjectDisplay?.force && forcedProjectDisplay.slice) {
      return { ...storageSettings, ...forcedProjectDisplay.slice };
    }
    return storageSettings;
  }, [storageSettings, forcedProjectDisplay]);

  const syncForcedProjectDisplay = useCallback((payload: ForcedProjectDisplayState) => {
    setForcedProjectDisplay(payload);
  }, []);

  const updateSetting = useCallback(
    <K extends keyof UserSettings>(key: K, value: UserSettings[K]) => {
      if (forcedProjectDisplay?.force && isProjectDisplaySettingKey(key)) {
        return;
      }
      if (key === "locationCoordinateDisplays") {
        const v = value as LocationCoordinateDisplays;
        if (!hasAtLeastOneCoordinateDisplay(v)) {
          return;
        }
      }
      setStorageSettings((prev) => {
        const next = { ...prev, [key]: value };
        saveSettings(next);
        return next;
      });
    },
    [forcedProjectDisplay],
  );

  const resetSettings = useCallback(() => {
    setStorageSettings(defaultUserSettings);
    saveSettings(defaultUserSettings);
  }, []);

  if (!ready) return null;

  return (
    <UserProfileContext.Provider
      value={{
        settings,
        updateSetting,
        resetSettings,
        syncForcedProjectDisplay,
      }}
    >
      {children}
    </UserProfileContext.Provider>
  );
}

export function useUserProfile(): UserProfileContextValue {
  const ctx = useContext(UserProfileContext);
  if (!ctx) throw new Error("useUserProfile must be used within UserProfileProvider");
  return ctx;
}
